import type { GiverState } from '../types';

/**
 * DID THIS LISTENER'S CALL COME FROM THE 1:1 QUEUE? The one fact the after-call
 * path asks before it puts anybody back in the queue (2026-10-29).
 *
 * The listener's seat reaches a call by several roads: the queue's own match
 * (Give Empathy, then WAITING, then PINGED), the in-app ring and its push
 * (`acceptRing`), a cohort request card's Call (`adoptClaimedCall`), a booked
 * call starting. Only the first is a person who asked to be in the queue. The
 * after-call path used to rejoin for all of them, keyed on whether the page's
 * preflight had left `giverPreflightResults` behind: so a ring call ended in
 * 'Session ended. Please click "Give Empathy" again to rejoin the queue.'
 * (nothing stored), and a ring call taken after an earlier queue visit put the
 * listener in a queue they had left (something stored). The stored check says
 * the device CAN rejoin, not that the person meant to.
 *
 * So the road is recorded where every adopted session passes
 * (`setCurrentSession`, class D: it sees every writer), from the seat as it was
 * just before the call: WAITING or PINGED means the queue. Kept in
 * sessionStorage beside the stored check, so a reload in the middle of a queue
 * call does not forget it, and the store's sign-out reset clears it.
 */
const KEY = 'giverQueueCallId';

const QUEUE_SEATS: ReadonlySet<GiverState> = new Set<GiverState>(['WAITING', 'PINGED']);

/**
 * THE ID AS STORAGE HOLDS IT. The status read hands the store
 * `$activeSession->id`, a JSON NUMBER, whatever `GiverStatus.session_id`
 * declares; a claim hands it a string. sessionStorage keeps only strings, so
 * an id compared as it arrived never equals the one read back, and every
 * queue call read as "not from the queue".
 */
function key(sessionId: string | number): string {
  return String(sessionId);
}

function read(): string | null {
  try {
    return sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}

/**
 * A listener's session was just adopted. `seatBefore` is the giver seat the
 * store held at that moment, before the call's own state lands.
 *
 * The same session read again (a reload: a fresh store reads it from OFFLINE)
 * keeps what was recorded when it first arrived.
 */
export function noteGiverCall(sessionId: string | number, seatBefore: GiverState): void {
  if (read() === key(sessionId)) return;
  try {
    if (QUEUE_SEATS.has(seatBefore)) sessionStorage.setItem(KEY, key(sessionId));
    else sessionStorage.removeItem(KEY);
  } catch {
    // Storage unavailable: the call reads as not from the queue, and the
    // listener lands on the page, never in a queue they did not ask for.
  }
}

/** True only for the session that the queue itself matched this listener to. */
export function wasQueueCall(sessionId: string | number | null): boolean {
  return sessionId !== null && read() === key(sessionId);
}

/** Sign-out: the next account on this tab did not join anything. */
export function forgetGiverQueueCall(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing was kept.
  }
}
